import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { useSelector } from 'react-redux'
import { Form, useFormikContext } from 'formik'

import {
  closeDialogActionCreator as closeDialog,
  openDialogActionCreator as openDialog,
  useCWQDialogContext,
} from '@user/components/SoldServices'
import { getSXStyles } from '../styles'
import { MainForm } from './MainForm/MainForm'
import { SettingsPage } from '@sales-center/components'
import { AddOpportunityFormProps } from './typings'
import { IAddOpportunityFormModel } from '../typings'
import useMediaQuery from '@mui/material/useMediaQuery'
import { TitleSection } from './FormComponents/TitleSection'
import { SoldServicesDrawer } from '../../SoldServicesDrawer'
import { SecondaryForm } from './SecondaryForm/SecondaryForm'
import { AddOpportunityFormHeader } from './AddOpportunityFormHeader/AddOpportunityFormHeader'
import { SOLD_SERVICES_CONTENT_MODE } from '../../SoldServicesDrawer/SoldServicesContent/typings'

import { selectAllBranches } from '@user/store/branches'
import { useGetSalesTeamPermissionMessages } from '@user/store/salesTeam'
import { useTheme } from '@mui/material/styles'

export function AddOpportunityForm(props: AddOpportunityFormProps) {
  const {
    billTo,
    innerRef,
    title,
    entityBranchId,
    entityId,
    entityType,
    externalSvcLocId,
    serviceLocations,
    type,
    noteCodes = [],
  } = props

  const [, dispatch] = useCWQDialogContext()

  const [hideOptional, setHideOptional] = useState(false)
  const [isClosedWon, setIsClosedWon] = useState<boolean>()
  const [isClosedLost, setIsClosedLost] = useState<boolean>()

  const { submitForm, isValid, validateForm, values, setValues } =
    useFormikContext<IAddOpportunityFormModel>()

  const { salesTeamId, note, referral, ownerId } = values

  const onSoldservicesDrawerSave = useCallback(
    async (input) => {
      setValues((v) => ({
        ...v,
        appData: input.appData,
        salesNote: input.salesNote,
      }))
      dispatch(closeDialog())
      submitForm()
    },
    [dispatch, setValues, submitForm],
  )

  const { disableSaveAndView } = useGetSalesTeamPermissionMessages(
    'success',
    +salesTeamId,
    ownerId,
  )

  const onSave = useCallback(async () => {
    await validateForm()
    if (isValid && isClosedWon) {
      dispatch(openDialog('ssq'))
    } else {
      submitForm()
    }
  }, [dispatch, isClosedWon, isValid, submitForm, validateForm])

  const onChangeHideOptional = useCallback(
    (_e, checked: boolean) => setHideOptional(checked),
    [],
  )

  const onClosedWonChange = useCallback(
    (isClosedWon: boolean, isClosedLost: boolean) => {
      setIsClosedWon(isClosedWon)
      setIsClosedLost(isClosedLost)
    },
    [],
  )

  const isMobile = useMediaQuery('(max-width:900px)')

  const styles = getSXStyles(isMobile)

  const branches = useSelector(selectAllBranches)

  const billToBranchId = useMemo(() => {
    // bill-to search endpoint (pestpac) does not return branch id, only branch name
    const branchByName = branches?.find((x) => x.name === billTo.branch)

    return branchByName?.id ?? ''
  }, [billTo.branch, branches])

  const branchId = entityBranchId ?? billToBranchId
  const theme = useTheme()
  return (
    <Form>
      <SettingsPage
        padding="0"
        style={{ backgroundColor: theme.palette.grey[50] }}
        hideTitleOnScroll={true}
        headContent={
          <AddOpportunityFormHeader
            isClosedWon={isClosedWon}
            isClosedLost={isClosedLost}
            onSave={onSave}
            hideOptional={hideOptional}
            onChangeHideOptional={onChangeHideOptional}
            disableSaveAndView={disableSaveAndView}
            noteCodes={noteCodes}
          />
        }
        contentId="CreateOpportunityForm"
        ref={innerRef}
      >
        <Box sx={styles.centered}>
          <Box maxWidth={isMobile ? '550px' : '1100px'} width="100%">
            {!isMobile && (
              <TitleSection mt={2} mb={3}>
                {title}
              </TitleSection>
            )}

            <Grid
              sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
              container
              spacing={2}
            >
              <Grid item xs={12} md={7.5}>
                <MainForm
                  branchId={branchId}
                  hideOptional={hideOptional}
                  onClosedWonChange={onClosedWonChange}
                />
              </Grid>

              <Grid item xs={12} md={4.5}>
                <SecondaryForm
                  branchId={branchId}
                  hideOptional={hideOptional}
                  noteCodes={noteCodes}
                  {...props}
                />
              </Grid>
              {isMobile && (
                <Grid item xs={12} mt={isMobile ? 2 : undefined}>
                  <TitleSection>{title}</TitleSection>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </SettingsPage>
      <SoldServicesDrawer
        branchId={branchId}
        closedWonStageId={+values.opportunityStageId}
        entityId={entityId}
        entityType={entityType}
        externalSvcLocId={externalSvcLocId}
        onCloseWin={onSoldservicesDrawerSave}
        readonly={false}
        serviceLocations={serviceLocations}
        type={type?.valueOf() ?? SOLD_SERVICES_CONTENT_MODE.DuplicateOpp}
        salesNote={note}
        referral={referral ?? undefined}
      />
    </Form>
  )
}
